<template>
  <div class="expertview" v-if="dataWasFetched">
    <div class="expertview-header">
      <system-status
        class="system-status"
        :deviceData="latestPower3ParameterPublisherApp"
        :heartbeat="latestPower3DeviceHeartbeatApp"
        @systemStatus="updateSystemStatus"
        v-if="latestPower3ParameterPublisherApp"
      />

      <h2>{{ $t('Web.ExpertMode.Headline') }}</h2>
      <span class="serial">{{ $t('Web.ExpertMode.Serial') }} {{ shortSerial(device.serial) }}</span>
    </div>

    <div class="expertview-body">
      <div class="expertview-body__offline" v-if="deviceStatus === 'offline'">
        <p class="error">{{ $t('Web.ExpertMode.Messages.NoData') }}</p>
        <cactus-button :isPrimary="true" @btnClicked="retry()">
          {{ $t('Web.ExpertMode.Buttons.Retry') }}
        </cactus-button>
      </div>

      <div
        class="expertview-body__online expertview-body-editBlock"
        v-if="deviceStatus !== 'offline' && isTechnician"
      >
        <experview-writeable />

        <div class="expertview-body-saving">
          <div class="error" v-show="error && !savingSuccessfull">
            <p>{{ $t('Web.expert_view_save_settings_failure') }}</p>
          </div>

          <div class="success" v-if="savingSuccessfull">
            <p>{{ $t('Web.expert_view_save_settings_success') }}</p>
          </div>

          <button class="expertview-body__btn" @click="saveSettings" v-else>
            {{ $t('Web.expert_view_save_settings') }}
          </button>
        </div>
      </div>
    </div>

    <div class="expertview-footer" v-if="deviceStatus !== 'offline'">
      <read-only-values />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import _isEqual from 'lodash/isEqual';

import CactusButton from '../_common/cactusButton';
import SystemStatus from '../_common/systemStatus';
import UtilMixin from '@/helpers/mixins';
import ExperviewWriteable from '../expertview/index.vue';
import ReadOnlyValues from '../expertview/readOnlyValues';

const INTERVAL = 1000 * 60;
const ONE_SECOND = 1000;
const MAX_REQUESTS = 5;

export default {
  name: 'ExpertView',
  components: { CactusButton, SystemStatus, ExperviewWriteable, ReadOnlyValues },
  mixins: [UtilMixin],
  data() {
    return {
      interval: undefined,
      showModal: false,
      deviceStatus: undefined,
      dataWasFetched: false,
      savingSuccessfull: undefined,
      error: undefined,
      referenceObj: undefined
    };
  },
  mounted() {
    if (this.device) {
      this.$nextTick(() => {
        this.fetchData();
      });
    }
  },
  created() {
    if (this.device) {
      this.$nextTick(() => {
        this.fetchData();
      });
    }
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  computed: {
    ...mapGetters([
      'apiVersion',
      'device',
      'isTechnician',
      'latestGridMeterApp',
      'latestLgBatteryApp',
      'latestPvMeterApp',
      'latestPower3ParameterPublisherApp',
      'latestPowerInverterReadApp',
      'latestPowerInverterWriteApp',
      'getExpertviewParams',
      'latestPower3DeviceHeartbeatApp'
    ])
  },
  methods: {
    ...mapActions(['getApiVersion', 'getLatestAppData', 'submitExpertSettings']),

    saveSettings() {
      this.$log.info('ExpertView::saveSettings');

      // take value from store and save it. Also ensure to set App_Inverter_Config to true. Otherwise
      // the config will not get written to the inverter.
      const expertviewParams = {
        serial: this.device.serial,
        data: [
          {
            app: 'POWER3_PARAMETER_MANAGER_APP',
            version: '1.0',
            parameters: {
              App_Inverter_Config: true,
              ...this.getExpertviewParams[0].parameters
            }
          },
          {
            app: 'POWER_INVERTER_WRITE_APP',
            version: '1.0',
            parameters: {
              App_Inverter_Config: true,
              ...this.getExpertviewParams[1].parameters
            }
          }
        ]
      };

      this.$showGlobalLoadingSpinner();

      this.submitExpertSettings(expertviewParams)
        .then(() => {
          // call to graphql
          const queryObject = {
            deviceSerialNumber: this.device.serial,
            requestGranularity: '1h',
            version: '1.0'
          };

          // Check if config was really sent to device
          setTimeout(() => {
            this.verifyConfigSaving(queryObject, MAX_REQUESTS);
          }, ONE_SECOND * 2);
        })
        .catch(err => {
          this.$log.error('ExpertView::saveSettings -> error', err);
        });
    },

    verifyConfigSaving(queryObj, maxTries) {
      let configCheck = new Promise((resolve, reject) => {
        try {
          this.$showGlobalLoadingSpinner();
          this.getLatestAppData(queryObj)
            .then(response => {
              const responseData =
                {
                  ...response.POWER3_PARAMETER_MANAGER_APP.parameters,
                  ...response.POWER_INVERTER_WRITE_APP.parameters
                } || {};

              if (_isEqual(this.referenceObj, responseData)) {
                if (maxTries > 0) {
                  maxTries -= 1;
                  // Wait for two seconds before refetching data from device
                  setTimeout(() => {
                    this.verifyConfigSaving(queryObj, maxTries);
                  }, ONE_SECOND * 2);
                } else {
                  reject(new Error('config not saved. timed out'));
                }
              } else {
                resolve('success');
              }
            })
            .catch(e => {
              console.error('ExpertView::verifyConfigSaving -> error', e.message);
            });
        } catch (err) {
          reject(err);
        }
      });

      configCheck.then(
        val => {
          if (val) {
            this.savingSuccessfull = true;
            this.$hideGlobalLoadingSpinner();
            setTimeout(() => {
              this.savingSuccessfull = false;
            }, ONE_SECOND * 4);
          }
        },
        () => {
          this.error = true;
          this.$hideGlobalLoadingSpinner();
        }
      );
    },

    fetchData() {
      const currentDataPromise = this.getLatestAppData(
        this.getQueryObject(this.device.serial)
      ).then(data => {
        this.referenceObj = JSON.parse(
          JSON.stringify({
            ...data.POWER3_PARAMETER_MANAGER_APP.parameters,
            ...data.POWER_INVERTER_WRITE_APP.parameters
          })
        );
        return data;
      });
      const currentApiPromise = this.getApiVersion().then(data => data);

      this.$showGlobalLoadingSpinner();
      Promise.all([currentDataPromise, currentApiPromise])
        .then(() => {
          this.pollDeviceData(INTERVAL);
        })
        .finally(() => {
          this.$hideGlobalLoadingSpinner();
          this.dataWasFetched = true;
        });
    },

    updateSystemStatus(status) {
      this.deviceStatus = status;
      if (status === 'offline') {
        clearInterval(this.interval);
      }
    },

    retry() {
      this.$showGlobalLoadingSpinner();
      this.getLatestAppData(this.getQueryObject(this.device.serial)).finally(() => {
        this.$hideGlobalLoadingSpinner();
      });
    },

    pollDeviceData(time) {
      time = time ? time : INTERVAL;
      clearInterval(this.interval);

      this.interval = setInterval(() => {
        if ((this.device || {}).serial) {
          this.getCurrentData();
          this.pollDeviceData(time);
        }
      }, time);
    },

    getCurrentData() {
      this.getLatestAppData(this.getQueryObject(this.device.serial));
    },

    getQueryObject(sn) {
      return {
        deviceSerialNumber: sn,
        requestGranularity: '1h',
        requestStartTime: this.$moment(new Date())
          .startOf('day')
          .format('YYYY-MM-DD[T]HH:mm:ssZ'),
        requestEndTime: this.$moment(new Date())
          .endOf('day')
          .format('YYYY-MM-DD[T]HH:mm:ssZ'),
        version: '1.0'
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.expertview {
  max-width: $maxWidth;
  margin: 0 auto;
  padding-bottom: 100px;
}

.expertview-header {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;

  h2 {
    @include headlineLight;
    color: $greeny-blue;
    margin-top: 25px;
  }

  .serial {
    @include unitSmall;
    color: $charcoal-grey;
    margin-top: 10px;
  }
}

.expertview-body {
  margin-top: 60px;

  .error {
    color: $charcoal-grey;
  }

  .success {
    color: $white;
    text-align: center;
    margin: 50px 0 38px 0;
    font-size: 15px;
    background: $charcoal-grey;
    width: 100%;
    border-radius: 0;

    p {
      text-align: center;
      line-height: 48px;
    }
  }

  &-saving {
    margin-top: 40px;
  }

  &__offline {
    text-align: center;
  }
}

.expertview-footer {
  margin-top: 60px;
}
</style>
